import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { attendanceApi, employeeApi } from '@/lib/api';
import { Attendance, User } from '@/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { formatDate, formatTime, getStatusColor } from '@/lib/utils';
import { CalendarIcon, ClockIcon, MagnifyingGlassIcon, UserIcon } from '@heroicons/react/24/outline';

export default function AttendancePage() {
  const { user } = useAuth();
  const [attendanceRecords, setAttendanceRecords] = useState<Attendance[]>([]);
  const [employees, setEmployees] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  // const [employeesLoading, setEmployeesLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  // const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState<string>('');

  console.log(attendanceRecords,"=====9999999")

  useEffect(() => {
    // Set default date range based on user role
    const now = new Date();
    
    if (user?.role === 'ADMIN' || user?.role === 'SUPERADMIN') {
      // For admin, show current date by default
      const today = now.toISOString().split('T')[0];
      setStartDate(today);
      setEndDate(today);
    } else {
      // For other roles, show current month
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      
      setStartDate(firstDay.toISOString().split('T')[0]);
      setEndDate(lastDay.toISOString().split('T')[0]);
    }

    // Load employees for admin/HR users
    if (user && (user.role === 'ADMIN' || user.role === 'HR')) {
      fetchEmployees();
    }
  }, [user?.role]);

  useEffect(() => {
    if (startDate && endDate && user) {
      fetchAttendance();
    }
  }, [page, startDate, endDate, employeeId, statusFilter, user]);

  // Real-time search effect
  useEffect(() => {
    if (searchQuery || employeeName || statusFilter) {
      const timer = setTimeout(() => {
        fetchAttendance();
      }, 300); // Debounce search
      
      return () => clearTimeout(timer);
    }
  }, [searchQuery, employeeName, statusFilter]);

  // Clear selected date range when dates are manually changed
  useEffect(() => {
    if (startDate && endDate) {
      // Check if the current dates match any of the quick ranges
      const now = new Date();
      const currentStart = new Date(startDate);
      const currentEnd = new Date(endDate);
      
      // Check if it matches "This Week"
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      
      // Check if it matches "This Month"
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      
      // Check if it matches "This Quarter"
      const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
      const quarterEnd = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 + 3, 0);
      
      const isWeek = currentStart.toDateString() === weekStart.toDateString() && 
                    currentEnd.toDateString() === weekEnd.toDateString();
      const isMonth = currentStart.toDateString() === monthStart.toDateString() && 
                     currentEnd.toDateString() === monthEnd.toDateString();
      const isQuarter = currentStart.toDateString() === quarterStart.toDateString() && 
                       currentEnd.toDateString() === quarterEnd.toDateString();
      
      if (isWeek) {
        setSelectedDateRange('week');
      } else if (isMonth) {
        setSelectedDateRange('month');
      } else if (isQuarter) {
        setSelectedDateRange('quarter');
      } else {
        setSelectedDateRange('');
      }
    }
  }, [startDate, endDate]);

  const fetchEmployees = async () => {
    try {
      // setEmployeesLoading(true);
      const response = await employeeApi.getAll({
        page: 1,
        limit: 1000, // Get all employees for dropdown
      });
      
      if (response.success && response.data) {
        setEmployees(response.data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      // setEmployeesLoading(false);
    }
  };

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const isAdminOrHR = user?.role === 'ADMIN' || user?.role === 'HR' || user?.role === 'SUPERADMIN';
      
      const response = isAdminOrHR 
        ? await attendanceApi.getAllAttendance({
            page,
            limit: 20,
            startDate,
            endDate,
            employeeId: employeeId || undefined,
          })
        : await attendanceApi.getMyAttendance({
            page,
            limit: 20,
            startDate,
            endDate,
          });

      if (response.success && response.data) {
        let filteredRecords = response.data;
        
        // Apply client-side filtering for status and employee name
        if (statusFilter) {
          filteredRecords = filteredRecords.filter(record => record.status === statusFilter);
        }
        
        if (employeeName && isAdminOrHR) {
          const matchingEmployee = employees.find(emp => 
            `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(employeeName.toLowerCase()) ||
            emp.employeeId.toLowerCase().includes(employeeName.toLowerCase())
          );
          if (matchingEmployee) {
            filteredRecords = filteredRecords.filter(record => record.employeeId === matchingEmployee.employeeId);
          } else {
            filteredRecords = [];
          }
        }
        
        if (searchQuery) {
          filteredRecords = filteredRecords.filter(record => {
            return record.employeeId.toLowerCase().includes(searchQuery.toLowerCase());
          });
        }
        
        setAttendanceRecords(filteredRecords);
        if (response.pagination) {
          setTotalPages(response.pagination.pages);
          setTotalRecords(response.pagination.total);
        }
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
    } finally {
      setLoading(false);
    }
  };
  console.log(attendanceRecords,"uiioio999")

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchAttendance();
  };

  const handleReset = () => {
    setEmployeeId('');
    setEmployeeName('');
    setStatusFilter('');
    setSearchQuery('');
    setSelectedDateRange('');
    
    // Reset to current month
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
    setPage(1);
  };

  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find(emp => emp.employeeId === employeeId);
    return employee ? `${employee.firstName} ${employee.lastName}` : employeeId;
  };

  const getEmployeeDepartment = (employeeId: string) => {
    const employee = employees.find(emp => emp.employeeId === employeeId);
    return employee ? employee.department : '';
  };

  const setDateRange = (range: 'today' | 'week' | 'month' | 'quarter') => {
    const now = new Date();
    let start: Date, end: Date;

    switch (range) {
      case 'today':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'week':
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        start = weekStart;
        end = weekEnd;
        break;
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'quarter':
        const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        const quarterEnd = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 + 3, 0);
        start = quarterStart;
        end = quarterEnd;
        break;
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
    setSelectedDateRange(range);
  };

  const exportToCSV = () => {
    const headers = [
      'Date',
      ...(user?.role === 'ADMIN' || user?.role === 'HR' || user?.role === 'SUPERADMIN' ? ['Employee ID', 'Employee Name', 'Department'] : []),
      'Check In',
      'Check Out',
      'Working Hours',
      'Status',
      'Check In Location',
      'Manual Entry'
    ];

    const rows = attendanceRecords.map(record => [
      formatDate(new Date(record.date)),
      ...(user?.role === 'ADMIN' || user?.role === 'HR' || user?.role === 'SUPERADMIN' ? [
        record.employeeId,
        getEmployeeName(record.employeeId),
        getEmployeeDepartment(record.employeeId)
      ] : []),
      record.checkIn ? formatTime(new Date(record.checkIn)) : '',
      record.checkOut ? formatTime(new Date(record.checkOut)) : '',
      record.workingHours ? record.workingHours.toFixed(1) : '',
      record.status,
      record.checkInLocation?.address || '',
      record.isManualEntry ? 'Yes' : 'No'
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `attendance-records-${startDate}-to-${endDate}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="overflow-x-auto space-y-6 pb-8">
     
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Attendance Records</h1>
        <p className="text-gray-600">
          {user?.role === 'ADMIN' || user?.role === 'HR' || user?.role === 'SUPERADMIN'
            ? 'View and manage attendance records for all employees'
            : 'View your attendance history and records'
          }
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MagnifyingGlassIcon className="h-5 w-5" />
            Search & Filter Records
          </CardTitle>
          <CardDescription>
            Filter attendance records by date range, employee, status, and more
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Employee Code Search - Only for Admin and HR */}
         {(user?.role === 'ADMIN' || user?.role === 'HR' || user?.role === 'SUPERADMIN') && (
                    <div>
                      <label htmlFor="employeeId" className="block text-sm font-medium text-gray-700 mb-1">
                        Employee ID
                      </label>
                      <Input
                        id="employeeId"
                        type="text"
                        placeholder="Enter employee ID"
                        value={employeeId}
                        onChange={(e) => setEmployeeId(e.target.value)}
                      />
                    </div>
                  )}

          <form onSubmit={handleFilter} className="space-y-4">
            {/* Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </div>
              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Date Range Shortcuts */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quick Date Ranges
              </label>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant={selectedDateRange === 'week' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDateRange('week')}
                >
                  This Week
                </Button>
                <Button
                  type="button"
                  variant={selectedDateRange === 'month' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDateRange('month')}
                >
                  This Month
                </Button>
                <Button
                  type="button"
                  variant={selectedDateRange === 'quarter' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDateRange('quarter')}
                >
                  This Quarter
                </Button>
              </div>
            </div>

            {/* Advanced Filters Toggle */}
            {/* <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="text-sm"
              >
                {showAdvancedFilters ? 'Hide' : 'Show'} Advanced Filters
              </Button>
              <div className="text-sm text-gray-500">
                Found {attendanceRecords.length} records
              </div>
            </div> */}

            {/* Advanced Filters */}
           

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <Button type="submit" className="flex items-center gap-2">
                <MagnifyingGlassIcon className="h-4 w-4" />
                Apply Filters
              </Button>
              <Button type="button" variant="outline" onClick={handleReset}>
                Reset Filters
              </Button>
              {attendanceRecords.length > 0 && (
                <Button type="button" variant="outline" onClick={exportToCSV} className="flex items-center gap-2">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export CSV
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Attendance Summary - Hidden for Admin and HR */}
      {user?.role !== 'ADMIN' && user?.role !== 'HR' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Records</CardTitle>
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{attendanceRecords.length}</div>
              <p className="text-xs text-muted-foreground">
                Out of {totalRecords} total records
              </p>
            </CardContent>
          </Card> */}

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Present Days</CardTitle>
              <ClockIcon className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {attendanceRecords.filter(record => record.status === 'present').length}
              </div>
              {/* <p className="text-xs text-muted-foreground">
                {attendanceRecords.length > 0 ? 
                  `${((attendanceRecords.filter(record => record.status === 'present').length / attendanceRecords.length) * 100).toFixed(1)}% attendance` 
                  : '0% attendance'
                }
              </p> */}
            </CardContent>
          </Card>

          {/* <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Hours</CardTitle>
              <ClockIcon className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {attendanceRecords.reduce((total, record) => total + (record.workingHours || 0), 0).toFixed(1)}h
              </div>
              <p className="text-xs text-muted-foreground">Hours worked</p>
            </CardContent>
          </Card> */}

          {/* <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Hours/Day</CardTitle>
              <ClockIcon className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {attendanceRecords.length > 0 
                  ? (attendanceRecords.reduce((total, record) => total + (record.workingHours || 0), 0) / attendanceRecords.length).toFixed(1)
                  : '0.0'
                }h
              </div>
              <p className="text-xs text-muted-foreground">Average per day</p>
            </CardContent>
          </Card> */}
        </div>
      )}

      {/* Additional Statistics for Admin/HR/SuperAdmin */}
      {(user?.role === 'ADMIN' || user?.role === 'HR' || user?.role === 'SUPERADMIN') && attendanceRecords.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Status Breakdown</CardTitle>
            <CardDescription>Distribution of attendance statuses</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {attendanceRecords.filter(record => record.status === 'present').length}
                </div>
                <div className="text-sm text-green-700">Present</div>
              </div>
              {/* <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">
                  {attendanceRecords.filter(record => record.status === 'absent').length}
                </div>
                <div className="text-sm text-red-700">Absent</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">
                  {attendanceRecords.filter(record => record.status === 'late').length}
                </div>
                <div className="text-sm text-yellow-700">Late</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">
                  {attendanceRecords.filter(record => record.status === 'half_day').length}
                </div>
                <div className="text-sm text-orange-700">Half Day</div>
              </div> */}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Attendance Records */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Attendance Records</span>
              {(user?.role === 'ADMIN' || user?.role === 'HR' || user?.role === 'SUPERADMIN') && (
                <div className="text-sm text-gray-500">
                  Showing {attendanceRecords.length} of {totalRecords} records
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {attendanceRecords.length === 0 ? (
              <div className="text-center py-12">
                <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <CalendarIcon className="h-12 w-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No attendance records found</h3>
                <p className="text-gray-600 mb-4">
                  {searchQuery || employeeName || statusFilter 
                    ? 'Try adjusting your search criteria or filters.' 
                    : 'No attendance records exist for the selected date range.'
                  }
                </p>
                {(searchQuery || employeeName || statusFilter) && (
                  <Button variant="outline" onClick={handleReset}>
                    Clear All Filters
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="relative overflow-x-auto max-h-[600px] overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap bg-gray-50">
                          Date
                        </th>
                        {(user?.role === 'ADMIN' || user?.role === 'HR' || user?.role === 'SUPERADMIN') && (
                          <>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap bg-gray-50">
                              Employee
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap bg-gray-50">
                              Department
                            </th>
                          </>
                        )}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap bg-gray-50">
                          Check In
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap bg-gray-50">
                          Check Out
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap bg-gray-50">
                          Hours
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap bg-gray-50">
                          Status
                        </th>
                        {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Location
                        </th> */}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {attendanceRecords.map((record) => (
                        <tr key={record._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(new Date(record.date))}
                          </td>
                          {(user?.role === 'ADMIN' || user?.role === 'HR' || user?.role === 'SUPERADMIN') && (
                            <>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <UserIcon className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {/* {getEmployeeName(record.employee._id)} */}
                                      {record.employee.firstName} {record.employee.lastName}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      ID: {record.employee.employeeId}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {record.employee.department}
                              </td>
                            </>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {record.checkIn ? (
                              <div>
                                <div>{formatTime(new Date(record.checkIn))}</div>
                                {record.isManualEntry && (
                                  <span className="text-xs text-orange-600">Manual Entry</span>
                                )}
                              </div>
                            ) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {record.checkOut ? (
                              <div>
                                <div>{formatTime(new Date(record.checkOut))}</div>
                              </div>
                            ) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {record.workingHours ? (
                              <div className="font-medium">
                                {record.workingHours.toFixed(1)}h
                              </div>
                            ) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(record.status)}`}>
                              {record.status.replace('_', ' ')}
                            </span>
                          </td>
                              {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {record.checkInLocation ? (
                                  <div className="flex items-center">
                                    <MapPinIcon className="h-4 w-4 mr-1" />
                                    <span className="truncate max-w-24" title={record.checkInLocation.address}>
                                      {record.checkInLocation.address.split(',')[0]}
                                    </span>
                                  </div>
                                ) : '-'}
                              </td> */}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
                    <div className="flex flex-1 justify-between sm:hidden">
                      <Button
                        variant="outline"
                        onClick={() => setPage(page - 1)}
                        disabled={page <= 1}
                        size="sm"
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setPage(page + 1)}
                        disabled={page >= totalPages}
                        size="sm"
                      >
                        Next
                      </Button>
                    </div>
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Showing <span className="font-medium">{((page - 1) * 20) + 1}</span> to{' '}
                          <span className="font-medium">{Math.min(page * 20, totalRecords)}</span> of{' '}
                          <span className="font-medium">{totalRecords}</span> results
                        </p>
                      </div>
                      <div>
                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                          <Button
                            variant="outline"
                            onClick={() => setPage(1)}
                            disabled={page <= 1}
                            size="sm"
                            className="rounded-r-none"
                          >
                            First
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => setPage(page - 1)}
                            disabled={page <= 1}
                            size="sm"
                            className="rounded-none border-l-0"
                          >
                            Previous
                          </Button>
                          
                          {/* Page numbers */}
                          <div className="hidden md:flex">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              let pageNum;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (page <= 3) {
                                pageNum = i + 1;
                              } else if (page >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = page - 2 + i;
                              }
                              
                              return (
                                <Button
                                  key={pageNum}
                                  variant={page === pageNum ? "default" : "outline"}
                                  onClick={() => setPage(pageNum)}
                                  size="sm"
                                  className="rounded-none border-l-0"
                                >
                                  {pageNum}
                                </Button>
                              );
                            })}
                          </div>

                          <Button
                            variant="outline"
                            onClick={() => setPage(page + 1)}
                            disabled={page >= totalPages}
                            size="sm"
                            className="rounded-none border-l-0"
                          >
                            Next
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => setPage(totalPages)}
                            disabled={page >= totalPages}
                            size="sm"
                            className="rounded-l-none border-l-0"
                          >
                            Last
                          </Button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Empty state removed - now inside the table card */}
    </div>
  );
}
